import { Bell, AlertTriangle, Mail, MessageSquare, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminNotificationDiagnostics,
  type NotificationFailureRow,
  type SuppressedNotification,
  type MonthlyReportFailure,
} from '@/hooks/admin/useAdminNotificationDiagnostics';

function SimpleTable<T extends Record<string, unknown>>({ rows, columns, emptyText }: {
  rows: T[];
  columns: { key: string; label: string; render?: (row: T) => React.ReactNode }[];
  emptyText?: string;
}) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">{emptyText ?? 'None'}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          {columns.map(c => (
            <th key={c.key} className="text-left py-1.5 pr-3 font-medium">{c.label}</th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={(row.id as string) ?? i} className="border-b border-wt-border/50">
              {columns.map(c => (
                <td key={c.key} className="py-1.5 pr-3">
                  {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function severityVariant(sev: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (sev === 'critical') return 'destructive';
  if (sev === 'warning') return 'outline';
  return 'secondary';
}

export function NotificationDiagnosticsTab() {
  const { data: d, isLoading, error } = useAdminNotificationDiagnostics();

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {d && (
        <div className="space-y-4">
          {/* Health summary */}
          {d.summary.failed_email_7d === 0 && d.summary.failed_sms_7d === 0 && d.summary.failed_push_7d === 0 && d.monthly_report_failures.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-green-500 py-1">
              <CheckCircle2 className="h-4 w-4" />
              All notification delivery healthy — no failures in last 7 days.
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Total (7d)</p>
                <p className="text-lg font-semibold">{d.summary.total_7d}</p>
              </CardContent>
            </Card>
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Email Delivered</p>
                <p className="text-lg font-semibold text-green-500">{d.summary.delivered_email_7d}</p>
              </CardContent>
            </Card>
            <Card className={`border-wt-border bg-wt-bg-secondary ${d.summary.failed_email_7d > 0 ? 'border-wt-red-primary/30' : ''}`}>
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Failures (7d)</p>
                <p className={`text-lg font-semibold ${d.summary.failed_email_7d + d.summary.failed_sms_7d + d.summary.failed_push_7d > 0 ? 'text-wt-red-primary' : ''}`}>
                  {d.summary.failed_email_7d + d.summary.failed_sms_7d + d.summary.failed_push_7d}
                </p>
              </CardContent>
            </Card>
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Suppressed (7d)</p>
                <p className="text-lg font-semibold">{d.summary.suppressed_7d}</p>
              </CardContent>
            </Card>
          </div>

          {/* Channel breakdown */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs text-wt-text-muted font-normal">Channel Breakdown (7d)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="flex flex-wrap gap-4 text-xs">
                {(['email', 'sms', 'push'] as const).map(ch => {
                  const stats = d.channel_breakdown_7d[ch];
                  return (
                    <div key={ch} className="flex items-center gap-2">
                      {ch === 'email' ? <Mail className="h-3 w-3 text-wt-text-muted" /> :
                       ch === 'sms' ? <MessageSquare className="h-3 w-3 text-wt-text-muted" /> :
                       <Bell className="h-3 w-3 text-wt-text-muted" />}
                      <span className="font-medium capitalize">{ch}:</span>
                      <span className="text-green-500">{stats.delivered} delivered</span>
                      {stats.failed > 0 && <span className="text-wt-red-primary">{stats.failed} failed</span>}
                      {stats.attempted === 0 && <span className="text-wt-text-muted">none attempted</span>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Audience breakdown */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs text-wt-text-muted font-normal">Audience Breakdown (7d)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="flex flex-wrap gap-2">
                {Object.entries(d.audience_breakdown_7d).map(([aud, count]) => (
                  <Badge key={aud} variant="secondary" className="text-xs gap-1">
                    {aud}: {count}
                  </Badge>
                ))}
                {Object.keys(d.audience_breakdown_7d).length === 0 && (
                  <p className="text-xs text-wt-text-muted">No notifications in last 7 days</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent failures */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-wt-red-primary" />
                Delivery Failures — 30d ({d.recent_failures.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <SimpleTable<NotificationFailureRow>
                rows={d.recent_failures}
                emptyText="No delivery failures in last 30 days"
                columns={[
                  { key: 'event_kind', label: 'Event', render: r => <span className="font-mono">{r.event_kind ?? '—'}</span> },
                  { key: 'severity', label: 'Sev', render: r => (
                    <Badge variant={severityVariant(r.severity)} className="text-[10px]">{r.severity}</Badge>
                  )},
                  { key: 'property_name', label: 'Property' },
                  { key: 'audience_type', label: 'Audience' },
                  { key: 'email_error_text', label: 'Email Error', render: r => r.email_error_text ? (
                    <span className="text-wt-red-primary max-w-[150px] truncate block" title={r.email_error_text}>{r.email_error_text}</span>
                  ) : <>—</> },
                  { key: 'sms_error_text', label: 'SMS Error', render: r => r.sms_error_text ? (
                    <span className="text-wt-red-primary max-w-[150px] truncate block" title={r.sms_error_text}>{r.sms_error_text}</span>
                  ) : <>—</> },
                  { key: 'created_at', label: 'When', render: r => formatDateTime(r.created_at) },
                ]}
              />
            </CardContent>
          </Card>

          {/* Recent suppressed */}
          {d.recent_suppressed.length > 0 && (
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium">Suppressed Notifications — 7d ({d.recent_suppressed.length})</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<SuppressedNotification>
                  rows={d.recent_suppressed}
                  columns={[
                    { key: 'event_kind', label: 'Event', render: r => <span className="font-mono">{r.event_kind ?? '—'}</span> },
                    { key: 'severity', label: 'Sev', render: r => (
                      <Badge variant={severityVariant(r.severity)} className="text-[10px]">{r.severity}</Badge>
                    )},
                    { key: 'property_name', label: 'Property' },
                    { key: 'suppression_reason', label: 'Reason', render: r => (
                      <Badge variant="outline" className="text-[10px] font-mono">{r.suppression_reason ?? '—'}</Badge>
                    )},
                    { key: 'created_at', label: 'When', render: r => formatDateTime(r.created_at) },
                  ]}
                />
              </CardContent>
            </Card>
          )}

          {/* Monthly report failures */}
          {d.monthly_report_failures.length > 0 && (
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4 text-wt-red-primary" />
                  Monthly Report Failures — 30d ({d.monthly_report_failures.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<MonthlyReportFailure>
                  rows={d.monthly_report_failures}
                  columns={[
                    { key: 'property_name', label: 'Property' },
                    { key: 'recipient_emails', label: 'Recipients', render: r => (
                      <span className="max-w-[200px] truncate block">{r.recipient_emails?.join(', ') ?? '—'}</span>
                    )},
                    { key: 'sent_at', label: 'Failed At', render: r => formatDateTime(r.sent_at) },
                  ]}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </AdminTabShell>
  );
}
